import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { isSuperAdminEmail } from "../../../shared/admin";
import { cloudCall } from "../../../shared/api";
import { readSession } from "../lib/kv";
import { IMP_KEY, SESSION_KEY } from "../lib/impersonation";

type SessionUser = { id: string; email: string; name: string; token: string };
type Account = {
  id: string;
  email: string;
  name: string;
  verified?: boolean;
  blocked?: boolean;
  is_admin?: boolean;
};
type Review = { id: string; name?: string; stars?: number; text?: string };
type ApkVersion = { version: string; is_current?: boolean; description?: string };

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [versions, setVersions] = useState<ApkVersion[]>([]);
  const [stats, setStats] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void readSession().then((session) => {
      setUser(session);
      setReady(true);
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!user?.token) return;
    setBusy(true);
    setError("");
    try {
      const data = await cloudCall<{
        accounts?: Account[];
        users?: Account[];
        reviews?: Review[];
        stats?: Record<string, number>;
      }>("/admin/overview", { token: user.token });
      setAccounts(data.accounts || data.users || []);
      const s = data.stats || {};
      setStats(
        `${s.users || 0} usuarios · ${s.verified || 0} verificados · ${s.blocked || 0} bloqueados`,
      );
      try {
        const landing = await fetch("https://rycso.es/api/admin/site-reviews?app=hermes", {
          headers: { Authorization: `Bearer ${user.token}`, "X-Rycso-App": "hermes", Accept: "application/json" },
        }).then(async (r) => {
          const body = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(body.error || "reseñas");
          return body as { reviews?: Review[] };
        });
        setReviews(landing.reviews || []);
      } catch {
        setReviews(data.reviews || []);
      }
      try {
        const apk = await fetch("https://rycso.es/api/admin/apk-versions?app=hermes", {
          headers: { Authorization: `Bearer ${user.token}`, "X-Rycso-App": "hermes", Accept: "application/json" },
        }).then(async (r) => {
          const body = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(body.error || "apk");
          return body as { versions?: ApkVersion[] };
        });
        setVersions(apk.versions || []);
      } catch {
        try {
          const hist = await fetch("https://rycso.es/hermes/apk/apk-history.json").then((r) => r.json());
          setVersions(
            (hist.versions || []).map((x: { version: string; is_current?: boolean; description?: string }) => ({
              version: x.version,
              is_current: x.is_current || x.version === hist.current_version,
              description: x.description,
            })),
          );
        } catch {
          setVersions([]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setBusy(false);
    }
  }, [user?.token]);

  useEffect(() => {
    if (user && isSuperAdminEmail(user.email)) void refresh();
  }, [user, refresh]);

  if (!ready) return <p className="coords">Cargando…</p>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isSuperAdminEmail(user.email)) {
    return (
      <div className="page-planes">
        <p className="empty">Solo administradores.</p>
        <Link to="/">Volver</Link>
      </div>
    );
  }

  const act = async (ident: string, action: "delete" | "block" | "unblock") => {
    if (action === "delete" && !window.confirm("¿Eliminar usuario?")) return;
    setBusy(true);
    try {
      const path = action === "delete" ? "/admin/users/delete" : `/admin/users/${action}`;
      await cloudCall(path, {
        method: "POST",
        token: user.token,
        body: JSON.stringify({ ident }),
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const delReview = async (id: string) => {
    if (!window.confirm("¿Eliminar reseña?")) return;
    try {
      await fetch("https://rycso.es/api/admin/site-reviews/delete", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "X-Rycso-App": "hermes",
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ app: "hermes", id }),
      }).then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || "No se pudo eliminar");
      });
    } catch {
      await cloudCall("/admin/site-reviews/delete", {
        method: "POST",
        token: user.token,
        body: JSON.stringify({ id }),
      });
    }
    await refresh();
  };

  const connectAs = async (account: Account) => {
    if (account.email.toLowerCase() === user.email.toLowerCase()) return;
    const testMode = window.confirm(
      "¿Activar modo de pruebas?\n\nAceptar: cambios no permanentes hasta desconectar.\nCancelar: se pedirá confirmación para entrar en vivo.",
    );
    let useTest = testMode;
    if (!testMode) {
      if (!window.confirm("¿Entrar sin modo de pruebas? Los cambios se guardarán en vivo.")) return;
      useTest = false;
    }
    setBusy(true);
    try {
      const data = await cloudCall<{
        impersonation_id?: string;
        session_token?: string;
        user?: SessionUser;
        test_mode?: boolean;
        admin_token?: string;
        target_email?: string;
      }>("/admin/impersonate/start", {
        method: "POST",
        token: user.token,
        body: JSON.stringify({ ident: account.id, test_mode: useTest }),
      });
      if (!data.session_token || !data.user) throw new Error("Respuesta incompleta");
      localStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          id: data.user.id,
          email: data.user.email,
          name: data.user.name || data.user.email,
          token: data.session_token,
        }),
      );
      sessionStorage.setItem(
        IMP_KEY,
        JSON.stringify({
          impersonationId: String(data.impersonation_id),
          adminToken: String(data.admin_token || user.token),
          adminEmail: user.email,
          testMode: Boolean(data.test_mode),
          targetEmail: String(data.target_email || account.email),
        }),
      );
      navigate("/");
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo conectar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-planes">
      <section className="planes-hero">
        <p className="eyebrow">ADMIN</p>
        <h1 className="section-title">Panel Hermes</h1>
        <p className="hero-meta">{stats || "Usuarios, reseñas y APK."}</p>
        {error ? <p className="empty">{error}</p> : null}
        <div className="toggle-row">
          <button type="button" className="btn-copper" onClick={() => void refresh()} disabled={busy}>
            {busy ? "Cargando…" : "Actualizar"}
          </button>
          <Link to="/" className="btn-ink">
            Volver
          </Link>
          <a className="btn-ink" href="/hermes/admin/">
            Panel ops
          </a>
        </div>
      </section>

      <section className="plan-list">
        <header className="list-head">
          <h2>Usuarios</h2>
        </header>
        <ul>
          {accounts.map((account) => (
            <li key={account.id}>
              <div className="plan-row">
                <div>
                  <h3>{account.name || account.email}</h3>
                  <p>
                    {account.email}
                    {account.verified ? " · verificado" : " · sin verificar"}
                    {account.blocked ? " · bloqueado" : ""}
                    {account.is_admin ? " · admin" : ""}
                  </p>
                  <div className="plan-meta">
                    {account.email.toLowerCase() !== user.email.toLowerCase() ? (
                      <button type="button" className="btn-ink" disabled={busy} onClick={() => void connectAs(account)}>
                        Conectar
                      </button>
                    ) : (
                      <span>Sesión actual</span>
                    )}
                    {!account.is_admin ? (
                      <>
                        <button type="button" className="btn-ink" onClick={() => void act(account.id, account.blocked ? "unblock" : "block")}>
                          {account.blocked ? "Desbloquear" : "Bloquear"}
                        </button>
                        <button type="button" className="btn-copper" onClick={() => void act(account.id, "delete")}>
                          Eliminar
                        </button>
                      </>
                    ) : (
                      <span>protegido</span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="plan-list">
        <header className="list-head">
          <h2>Reseñas web</h2>
        </header>
        {!reviews.length ? <p className="empty">Sin reseñas.</p> : null}
        <ul>
          {reviews.map((review) => (
            <li key={review.id}>
              <div className="plan-row">
                <div>
                  <h3>
                    {review.name || "Anónimo"} · {review.stars || 0}★
                  </h3>
                  <p>{review.text}</p>
                  <button type="button" className="btn-copper" onClick={() => void delReview(review.id)}>
                    Eliminar
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="plan-list">
        <header className="list-head">
          <h2>Versiones APK</h2>
        </header>
        {!versions.length ? <p className="empty">Sin historial.</p> : null}
        <ul>
          {versions.map((version) => (
            <li key={version.version}>
              <div className="plan-row">
                <div>
                  <h3>v{version.version}</h3>
                  <p>{version.is_current ? "Actual" : "Archivada"}{version.description ? ` · ${version.description}` : ""}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
